import { useCallback, useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '~/lib/utils';
import { usePrefersReducedMotion } from '~/landing_page/components/FeaturesSection/usePrefersReducedMotion';
import styles from './HeroAppFrame.module.css';

/**
 * The hero product shot — Figma "Placeholder for Container" 249:6800.
 *
 * The window chrome and the source rail, plus a strip of open deck files across the title bar. The
 * active file fills left to right over its dwell, and when the fill lands the next one takes over.
 * Any file can be clicked to jump to it, which restarts the fill on the one you picked.
 *
 * The body is still empty on purpose: the real editor is being designed. `DECK_CASES` is the seam
 * where per-case content hangs once it exists.
 */

const DECK_CASES = [
  'Pitch a startup',
  'Show design work',
  'Product update',
  'Explain an idea',
  'Explain a dev process',
] as const;

/* Figma order, top to bottom. */
const TOOL_ITEMS = [
  { src: '/landing/hero/tool-slide.svg', label: 'Slide' },
  { src: '/landing/hero/tool-document.svg', label: 'Document' },
  { src: '/landing/hero/tool-note.svg', label: 'Note' },
  { src: '/landing/hero/tool-image.svg', label: 'Image' },
  { src: '/landing/hero/tool-website.svg', label: 'Website' },
  { src: '/landing/hero/tool-cookbook.svg', label: 'Cookbooks' },
] as const;

const WINDOW_DOTS = ['red', 'yellow', 'green'] as const;

const DEFAULT_DWELL_MS = 4200;

export interface HeroAppFrameProps {
  className?: string;
  cases?: readonly string[];
  /** How long each file holds before the next one takes over. */
  dwellMs?: number;
  /**
   * What fills the window under the title bar, given the open file's index. Without it the window
   * stays the empty mock and keeps its placeholder source rail; with it, the rail is the live
   * editor's own and the mock one would be a second copy of it.
   */
  renderBody?: (activeIndex: number) => ReactNode;
  /** `dark` is the landing v2 window: same chrome, drawn on the app's dark ground. */
  tone?: 'light' | 'dark';
}

export function HeroAppFrame({
  className,
  cases = DECK_CASES,
  dwellMs = DEFAULT_DWELL_MS,
  renderBody,
  tone = 'light',
}: HeroAppFrameProps) {
  const baseId = useId();
  const [active, setActive] = useState(0);
  // Bumped on every activation, including a click on the file that is already open. It keys the
  // fill element, so remounting replays the sweep from zero instead of leaving it mid-travel.
  const [run, setRun] = useState(0);
  const reducedMotion = usePrefersReducedMotion();

  const goTo = useCallback((index: number) => {
    setActive(index);
    setRun((r) => r + 1);
  }, []);

  useEffect(() => {
    // Nothing advances on its own when motion is reduced: an auto-rotating strip is exactly the
    // moving content that preference is asking us to stop. Clicking still works.
    if (reducedMotion || cases.length <= 1) return;
    const timer = setTimeout(() => {
      setActive((i) => (i + 1) % cases.length);
      setRun((r) => r + 1);
    }, dwellMs);
    return () => clearTimeout(timer);
  }, [active, run, reducedMotion, dwellMs, cases.length]);

  return (
    <div
      className={cn(styles.frame, tone === 'dark' && styles.frameDark, className)}
      data-name="HeroAppFrame"
      // Drives the fill's duration, so the sweep and the handover are the same number.
      style={{ '--hero-dwell': `${dwellMs}ms` } as React.CSSProperties}
    >
      <div className={styles.titleBar}>
        <div className={styles.windowDots} aria-hidden="true">
          {WINDOW_DOTS.map((tone) => (
            <span key={tone} className={cn(styles.windowDot, styles[tone])} />
          ))}
        </div>

        <span className={styles.homeButton} aria-hidden="true">
          <img className={styles.homeIcon} src="/landing/hero/icon-home.svg" alt="" />
        </span>

        <div className={styles.tabs} role="tablist" aria-label="Deck examples">
          {cases.map((label, i) => {
            const isActive = i === active;
            return (
              <button
                key={label}
                type="button"
                role="tab"
                id={`${baseId}-tab-${i}`}
                className={cn(styles.tab, isActive && styles.tabActive)}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => goTo(i)}
              >
                {/* Sits under the label and sweeps across, so the pill reads as filling rather
                    than as a separate bar tacked on. */}
                {isActive && !reducedMotion && (
                  <span key={run} className={styles.tabProgress} aria-hidden="true" />
                )}
                {isActive && (
                  <img
                    className={styles.tabIcon}
                    src="/landing/hero/icon-deck-file.svg"
                    alt=""
                    aria-hidden="true"
                  />
                )}
                <span className={styles.tabLabel}>{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {renderBody ? (
        <div className={styles.body}>{renderBody(active)}</div>
      ) : (
        <div className={styles.toolRail} aria-hidden="true">
          {TOOL_ITEMS.map((item) => (
            <span key={item.label} className={styles.toolButton}>
              <img className={styles.toolIcon} src={item.src} alt="" />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

HeroAppFrame.displayName = 'HeroAppFrame';
